import { Controller } from "@hotwired/stimulus"

const TAB_STEPS = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }

export default class extends Controller {
  static targets = ["tab", "heading", "chart", "panel"]
  static values = {
    series: Array,
    active: String,
    library: Object
  }

  connect() {
    const initial = this.activeValue || this.seriesValue[0]?.name
    if (initial) this.show(initial)
  }

  disconnect() {
    this.destroyChart()
  }

  select(event) {
    event.preventDefault()
    const name = event.currentTarget.dataset.metricName
    if (name) this.show(name)
  }

  navigate(event) {
    const step = TAB_STEPS[event.key]
    if (!step) return

    event.preventDefault()
    const tabs = this.tabTargets
    const current = tabs.indexOf(event.currentTarget)
    const next = tabs[(current + step + tabs.length) % tabs.length]
    if (!next) return

    next.focus()
    this.show(next.dataset.metricName)
  }

  show(name) {
    const series = this.seriesValue.find((row) => row.name === name)
    if (!series || !this.hasChartTarget) return

    this.activeValue = name

    this.tabTargets.forEach((tab) => {
      const on = tab.dataset.metricName === name
      tab.setAttribute("aria-selected", on ? "true" : "false")
      tab.tabIndex = on ? 0 : -1
      tab.classList.toggle("bg-gray-900", on)
      tab.classList.toggle("text-white", on)
      tab.classList.toggle("border-gray-900", on)
      tab.classList.toggle("bg-white", !on)
      tab.classList.toggle("text-gray-700", !on)
      tab.classList.toggle("border-gray-200", !on)

      if (on && this.hasPanelTarget) {
        this.panelTarget.setAttribute("aria-labelledby", tab.id)
      }
    })

    if (this.hasHeadingTarget) {
      this.headingTarget.textContent = name
    }

    this.renderChart(series)
  }

  renderChart(series) {
    const Chartkick = window.Chartkick
    if (!Chartkick || !this.hasChartTarget) return

    this.destroyChart()

    const options = {
      colors: [series.color],
      legend: false,
      library: this.libraryFor(series)
    }
    if (series.unit) options.suffix = series.unit

    new Chartkick.LineChart(this.chartTarget.id, series.pairs, options)
  }

  // Counts get whole-number ticks; rate series keep decimals so a 3.5% axis is not rounded to 4%.
  libraryFor(series) {
    const base = this.libraryValue
    if (series.unit) return base

    const scales = base.scales || {}
    const y = scales.y || {}
    return {
      ...base,
      scales: { ...scales, y: { ...y, ticks: { ...(y.ticks || {}), precision: 0 } } }
    }
  }

  destroyChart() {
    if (!this.hasChartTarget) return

    const charts = window.Chartkick?.charts
    const id = this.chartTarget.id
    // Chartkick only clears its registry in destroyAll, so drop the entry by hand.
    if (charts && id in charts) {
      charts[id].destroy()
      delete charts[id]
    }
    this.chartTarget.replaceChildren()
  }
}
